/**
 * D-pak-parser for plukk (auto-pall-flyten).
 *
 * Bygger på den delte GS1-parseren, men godtar i tillegg AI 30 («Count of items»)
 * som antall — noen leverandører bruker den i stedet for AI 37. Utvidelsen sendes
 * inn per kall, slik at AI_DEFINITIONS i gs1Parser.ts er uendret for alle andre
 * kallsteder (mottak, telling, plassering osv.).
 *
 * Se «Claude md files/D-Pak-Pall-Nivaa3/Plan.md» (AP2).
 */

import { AIDefinition, parseGS1Barcode, looksLikeGS1Structure } from './gs1Parser';

/** Resultat av et D-pak-skann. */
export interface DPakScanData {
  /** Rå skannestreng, uendret. Brukes av duplikatvakten. */
  raw: string;
  gtin?: string;
  batchNo?: string;
  /** ISO-dato (YYYY-MM-DD) fra AI 15/17. */
  expiryDate?: string;
  /** Antall fra AI 37 eller AI 30. Undefined når etiketten ikke oppgir det. */
  quantity?: number;
  /**
   * Sann når skannet skal behandles som en D-pak: GTIN må finnes sammen med
   * batch og/eller holdbarhet. Er den usann, skal kalleren falle tilbake til
   * vanlig plukkflyt (beslutning 14 — blandede ordrer).
   */
  isDPak: boolean;
}

/**
 * AI 30 = Count of items. Variabel lengde, tolkes som antall.
 * Holdes bevisst som eneste tillegg — hver ekstra AI øker sjansen for at
 * findNextAI finner en falsk feltgrense i separatorløse strekkoder.
 */
const DPAK_EXTRA_AIS: AIDefinition[] = [
  { ai: '30', length: -1, field: 'quantity', description: 'Count of items', dataType: 'number' },
];

/**
 * Parser et skann som potensiell D-pak-etikett.
 * Kaster aldri — ugyldige/ukjente strekkoder gir `isDPak: false`.
 */
export function parseDPakBarcode(barcode: string): DPakScanData {
  const raw = barcode ?? '';

  if (!raw || !looksLikeGS1Structure(raw)) {
    return { raw, isDPak: false };
  }

  const gs1 = parseGS1Barcode(raw, undefined, DPAK_EXTRA_AIS);

  const gtin = gs1.gtin || undefined;
  const batchNo = gs1.batchNo || undefined;
  const expiryDate = gs1.expiryDate || gs1.useByDate || gs1.bestBeforeDate || undefined;
  const quantity =
    typeof gs1.quantity === 'number' && !isNaN(gs1.quantity) && gs1.quantity > 0
      ? gs1.quantity
      : undefined;

  // En D-pak må identifisere varen (GTIN) og bære sporbarhet (batch og/eller
  // holdbarhet). Rene GTIN-skann er vanlige vareskann og skal følge dagens flyt.
  const isDPak = !!gtin && (!!batchNo || !!expiryDate);

  return { raw, gtin, batchNo, expiryDate, quantity, isDPak };
}
